// src/components/modals/NpiSearchModal.js
import React, { useEffect, useMemo, useState } from "react";
import { getProviderByNpi } from "../APIs/providerApi";

function digitsOnly(v) {
  return String(v || "").replace(/\D/g, "");
}

function NpiSearchModal({ open, onClose, onFound }) {
  const [npi, setNpi] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const cleaned = useMemo(() => digitsOnly(npi), [npi]);
  const canSearch = cleaned.length === 10 && !loading;

  useEffect(() => {
    if (!open) return;
    // reset each time it opens
    setNpi("");
    setErr(null);
    setLoading(false);
  }, [open]);

  async function handleSearch() {
    setErr(null);

    const val = digitsOnly(npi);
    if (val.length !== 10) {
      setErr("Enter a valid 10-digit NPI.");
      return;
    }

    try {
      setLoading(true);
      const preview = await getProviderByNpi(val); // ProviderPreviewDTO
      // pass both preview + npi (needed later for claim redirect)
      onFound?.({ preview, npi: val });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setErr("No provider found for that NPI.");
      else setErr("Unable to look up that NPI. Try again.");
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && canSearch) handleSearch();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
        {/* Close (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-2 text-center">
          Find your profile
        </h2>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Enter your 10-digit NPI to locate your provider profile.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-2">
          NPI Number
        </label>

        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="1234567890"
            value={npi}
            onChange={(e) => setNpi(digitsOnly(e.target.value))}
            onKeyDown={onKeyDown}
            className="flex-1 h-10 rounded border px-3 bg-white text-gray-800 border-gray-300"
            maxLength={10}
          />

          <button
            type="button"
            onClick={handleSearch}
            disabled={!canSearch}
            className={`h-10 px-4 rounded ${
              canSearch
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
            title="Search"
            aria-label="Search"
          >
            {loading ? "..." : "Search"}
          </button>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          {cleaned.length}/10 digits
        </div>

        {err && <div className="mt-3 text-sm text-red-600">{err}</div>}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded border text-gray-700"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default NpiSearchModal;
